import { useNotifcation, setNotification } from "context/notification";
import apiClient from "lib/apiClient";

/**
 * Drop-in behavioral replacement for useAppServices()'s per-call wrapper
 * (CallService + useServiceHandler combined), built on the shared apiClient
 * instead of the ~550-endpoint lookup table in hook/services.js. Resolves to
 * {response, error} exactly like the old wrapper - never throws - so every
 * query/mutation hook keeps the same `if (response) {...} else {...}` shape
 * callers already use; only the transport underneath changes.
 *
 * Callers now pass the literal path ("/accounts/getbyid") instead of looking
 * it up via AppService.accounts.getbyid - see src/queries/*.js.
 */
function useRequest() {
  const [, dispatch] = useNotifcation();

  return async (method, path, { query, payload, token, apiKey, toaster, message, error, timeout } = {}) => {
    const url = query ? `${path}?${query}` : path;
    const headers = {};
    if (typeof token === "string" && token) headers["x-auth-token"] = `Bearer ${token}`;
    if (apiKey) headers.apiKey = apiKey;

    try {
      const res = await apiClient.request({
        method,
        url,
        data: payload,
        headers,
        ...(timeout ? { timeout } : {}),
      });

      if (toaster) {
        setNotification(dispatch, {
          open: true,
          message: message || res.data?.message,
          severity: "success",
          title: "Success",
        });
      }
      return { response: res.data };
    } catch (err) {
      // 401s never toast - the apiClient interceptor already redirects, same
      // as the old useServiceHandler's early return on UNAUTHORIZED.
      if (err.response?.status === 401) {
        return { error: err.response?.data || err };
      }

      const customError = err.response?.data?.error
        ? `${err.response?.data?.message} \n${err.response?.data?.error}`
        : err.response?.data?.message;

      if (toaster) {
        setNotification(dispatch, {
          open: true,
          message: error || customError || err.message,
          severity: "error",
          title: "Error",
        });
      }
      return { error: err.response ? err.response.data : err };
    }
  };
}

export default useRequest;
